<template>
  <ModalDialog
    header-text="Personal Notes"
    @close-modal="closeModal(false)"
    open
  >
    <template v-slot:content>
      <textarea
        class="personal-notes-textarea"
        v-model="notes"
        placeholder="Enter your personal notes here..."
        maxlength="500"
        data-cy="personal-notes-textarea"
      ></textarea>
      <Button
        class="personal-notes-button"
        type="button"
        theme-color="success"
        @click="saveNotesAsync"
        :disabled="value === notes"
        data-cy="personal-notes-save-button"
        >Save Note</Button
      >
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { useMainStore } from "@/stores/useMainStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { Button } from "@progress/kendo-vue-buttons";
import { ref } from "vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const emit = defineEmits<{
  (e: "closeModal", updated: boolean, newNotes: string): void;
}>();

const props = defineProps<{
  workflowId: number;
  value: string;
}>();

const notes = ref<string>(props.value);

const closeModal = function (updated: boolean, newNotes: string = "") {
  emit("closeModal", updated, newNotes);
};

const saveNotesAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("SetWorkflowPrivateComment", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      workflowId: props.workflowId,
      comment: notes.value,
    }),
    () => {
      notificationStore.notify({
        message: "Personal notes saved",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
      closeModal(true, notes.value);
    },
    "PersonalNotesModal",
    "saveNotesAsync",
    () => {
      notificationStore.notify({
        message: "Failed to save personal notes. Please try again",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};
</script>

<style lang="scss" scoped>
.personal-notes {
  &-textarea {
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin: 20px 0;
    max-width: 100%;

    @include media-breakpoint-up(sm) {
      width: 400px;
    }
  }

  &-button {
    margin: 0.25rem;
  }
}
</style>
